import { FC, useState } from 'react';
import {
  Button,
  ButtonProps,
  Icon,
  IconButton,
  MenuItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdDriveFileMoveOutline } from 'react-icons/md';
import {
  FileDetailsFileFragment,
  FileMoveDirectoryFragment,
  useMoveFileMutation,
} from '../../../../graphql/generated';
import { FileMoveBrowser } from './file-move-browser';

// Subset of common File props between file browser and file details queries
// that are needed for <FileMoveAction /> to work
export type FileMoveProps = Pick<
  FileDetailsFileFragment,
  | 'id'
  | 'cedant'
  | 'path'
  | 'name'
  | 'canMove'
  | 'breadcrumbs'
  | 'parentDirectoryId'
>;

type FileMoveActionProps = ButtonProps & {
  files: FileMoveProps[];
  buttonType?: 'MenuItem' | 'IconButton' | 'Button';
  onMoveComplete?: () => void;
};

export const FileMoveAction: FC<FileMoveActionProps> = ({
  files,
  buttonType = 'MenuItem',
  onMoveComplete,
  ...props
}) => {
  const [
    selectedDirectory,
    setSelectedDirectory,
  ] = useState<FileMoveDirectoryFragment | null>(null);
  const toast = useToast();
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();
  const [moveFile, { loading }] = useMoveFileMutation();

  const processResults = (results: PromiseSettledResult<any>[]) => {
    const successful: typeof files = [];
    const failed: typeof files = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.data) {
        successful.push(files[index]);
      } else {
        failed.push(files[index]);
      }
    });

    return { successful, failed };
  };

  const showSuccessToast = () => {
    toast({
      title: files.length === 1
        ? `Moved "${files[0].name}" to "${selectedDirectory?.name}"`
        : `Moved ${files.length} files to "${selectedDirectory?.name}"`,
      status: 'success',
      position: 'top',
    });
  };

  const showErrorToast = () => {
    toast({
      title: `Unable to move ${
        files.length === 1 ? `"${files[0].name}"` : `${files.length} files`
      }`,
      status: 'error',
      position: 'top',
    });
  };

  const showPartialSuccessToast = (successful: typeof files, failed: typeof files) => {
    const failedNames = failed.map((f) => f.name).join(', ');
    toast({
      title: `Moved ${successful.length} of ${files.length} files to "${selectedDirectory?.name}"`,
      description: `Failed to move: ${failedNames}`,
      status: 'warning',
      position: 'top',
    });
  };

  const handleFileMove = async () => {
    if (!selectedDirectory) return;

    try {
      const results = await Promise.allSettled(
        files.map((file) =>
          moveFile({
            variables: {
              cedant: file.cedant,
              input: {
                fileID: file.id,
                destinationDirectoryID: selectedDirectory.id,
              },
            },
            refetchQueries: ['GetFile', 'GetDirectory'],
          })
        )
      );

      const { successful, failed } = processResults(results);

      if (failed.length === 0) {
        showSuccessToast();
        closeModal();
        onMoveComplete?.();
      } else if (successful.length === 0) {
        showErrorToast();
        closeModal();
      } else {
        showPartialSuccessToast(successful, failed);
        closeModal();
        onMoveComplete?.();
      }
    } catch {
      showErrorToast();
      closeModal();
    }
  };

  const resetAndClose = () => {
    setSelectedDirectory(null);
    closeModal();
  };

  const isCurrentFileLocation =
    !selectedDirectory ||
    files.some((file) => file.path === selectedDirectory.path);

  const getModalTitle = () => {
    return files.length === 1 
      ? `Move "${files[0].name}" to...`
      : `Move ${files.length} files to...`;
  };

  const getButtonComponent = () => {
    const commonProps = {
      onClick: openModal,
      'data-testid': 'file-move-button',
    };

    if (buttonType === 'MenuItem') {
      return (
        <MenuItem
          {...commonProps}
          icon={<Icon as={MdDriveFileMoveOutline} fontSize="16" />}
        >
          Move
        </MenuItem>
      );
    }

    if (buttonType === 'IconButton') {
      return (
        <Tooltip label="Move" fontSize="xs">
          <IconButton
            {...commonProps}
            icon={<Icon as={MdDriveFileMoveOutline} fontSize="22" />}
            aria-label="Move"
            variant="clear"
          />
        </Tooltip>
      );
    }

    return (
      <Button
        {...commonProps}
        leftIcon={<Icon as={MdDriveFileMoveOutline} fontSize="18" />}
        {...props}
      >
        Move
      </Button>
    );
  };

  return (
    <>
      {getButtonComponent()}

      <Modal
        isOpen={isModalOpen}
        onClose={resetAndClose}
        isCentered
        size="2xl"
        // Prevent interrupting the operation on accidental click out
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent data-testid="file-move-modal">
          <ModalHeader pr={8}>{getModalTitle()}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FileMoveBrowser
              file={files[0]} // Use the first file for browsing
              selectDirectory={setSelectedDirectory}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={resetAndClose}
              data-testid="cancel-file-move-button"
            >
              Cancel
            </Button>
            <Button
              ml={3}
              colorScheme="green"
              disabled={isCurrentFileLocation}
              isLoading={loading}
              onClick={handleFileMove}
              data-testid="confirm-file-move-button"
            >
              Move
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};